/**
 * Product rows of the Order Amendments page and their translation into the
 * order-service amendment contract (`OrderAmendmentsDto`: `updateProducts[].patches`,
 * `addProducts[]`, `removeProducts[]`).
 *
 * order-service copies the patch values verbatim: it does not derive the net price
 * from the gross total and the tax class, nor the total from quantity × unit price.
 * Every money field is therefore computed here and sent complete.
 */

/** `ProductType` values order-service accepts (same list as `GET …/order-amendments/enums`). */
export const ORDER_PRODUCT_TYPES = [
  'GAS',
  'EV_CHARGE',
  'CAR_WASH',
  'PARKING',
  'CONSUMABLES',
  'CONSUMABLES_GIFT_CARD',
  'GIFT_CARD',
  'SUBSCRIPTION',
  'COMPOSITE',
  'UNKNOWN',
] as const;

/** Units as pop-data-feed sends them for receipt products; `COUNT` for everything else. */
export const PRODUCT_UNITS = ['LTR', 'KWH', 'COUNT', 'MIN'] as const;

const UNIT_BY_PRODUCT_TYPE: Record<string, string> = { GAS: 'LTR', EV_CHARGE: 'KWH' };

export const defaultUnitForType = (productType: string | undefined): string =>
  (productType && UNIT_BY_PRODUCT_TYPE[productType]) || 'COUNT';

export interface MoneyDto {
  amount: string;
  precision: number;
  currency: string;
}

export interface OrderProductLike {
  productExternalId?: string;
  name?: string;
  productType?: string;
  type?: string;
  paymentAttributes?: {
    currencyType?: string;
    taxClass?: number;
    unitPrice?: Partial<MoneyDto>;
    netPrice?: Partial<MoneyDto>;
    totalPrice?: Partial<MoneyDto>;
    quantity?: number;
    unit?: string;
    description?: string;
  };
  [key: string]: unknown;
}

/** One editable row of the product editor. */
export interface EditableProduct {
  productExternalId?: string;
  name: string;
  type: string;
  quantity: number | null;
  unit: string;
  unitPriceAmount: number | null;
  totalPriceAmount: number | null;
  currency: string;
  taxClass: number | null;
  _operation: 'add' | 'remove' | 'update' | null;
  /** Snapshot of the editable fields when the row was built, to detect changes. */
  _original?: string;
}

export interface ProductAmendments {
  updateProducts: Array<{ productExternalId: string; patches: Record<string, unknown> }>;
  addProducts: Array<Record<string, unknown>>;
  removeProducts: Array<{ productExternalId: string }>;
  /** Human-readable reasons why the rows cannot be sent; empty when valid. */
  errors: string[];
}

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const editableFields = (row: EditableProduct): string =>
  JSON.stringify([
    row.name,
    row.type,
    row.quantity,
    row.unit,
    row.unitPriceAmount,
    row.totalPriceAmount,
    row.currency,
    row.taxClass,
  ]);

/** Builds an editor row from an order-service product (as returned by `GET /orders/{uuid}`). */
export function toEditableProduct(
  product: OrderProductLike,
  defaultCurrency = 'EUR',
): EditableProduct {
  const pa = product.paymentAttributes ?? {};
  const type = String(product.productType ?? product.type ?? '');
  const row: EditableProduct = {
    productExternalId: product.productExternalId,
    name: String(product.name ?? ''),
    type,
    quantity: toNumber(pa.quantity),
    unit: pa.unit || defaultUnitForType(type),
    unitPriceAmount: toNumber(pa.unitPrice?.amount),
    totalPriceAmount: toNumber(pa.totalPrice?.amount),
    currency:
      pa.totalPrice?.currency || pa.unitPrice?.currency || pa.currencyType || defaultCurrency,
    taxClass: toNumber(pa.taxClass),
    _operation: null,
  };
  row._original = editableFields(row);
  return row;
}

/** A blank row for "Add Product". */
export function newEditableProduct(defaultCurrency = 'EUR', taxClass = 19): EditableProduct {
  return {
    productExternalId: undefined,
    name: '',
    type: '',
    quantity: 1,
    unit: 'COUNT',
    unitPriceAmount: null,
    totalPriceAmount: null,
    currency: defaultCurrency,
    taxClass,
    _operation: 'add',
  };
}

export const isRowChanged = (row: EditableProduct): boolean =>
  row._original !== undefined && row._original !== editableFields(row);

const decimals = (value: number): number => {
  const text = String(value);
  const index = text.indexOf('.');
  return index === -1 ? 0 : text.length - index - 1;
};

/** Money object as order-service expects it: string amount, explicit precision (2 … 3). */
export function money(amount: number, currency: string, minPrecision = 2): MoneyDto {
  const precision = Math.min(3, Math.max(minPrecision, decimals(amount)));
  return { amount: amount.toFixed(precision), precision, currency };
}

/** Net price from the gross total and the tax class in percent, three decimals. */
export function netFromGross(total: number, taxClass: number): number {
  return Number((total / (1 + taxClass / 100)).toFixed(3));
}

const randomSuffix = (): string => {
  const uuid = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '';
  return (uuid || Math.random().toString(36).slice(2)).replace(/-/g, '').slice(0, 8);
};

/** An id for a product added by hand, unique within the order and recognisable in Odoo and logs. */
export function newProductExternalId(name: string): string {
  const slug =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 24) || 'product';
  return `portal-${slug}-${randomSuffix()}`;
}

function validateRow(row: EditableProduct, label: string): string[] {
  const errors: string[] = [];
  if (!row.name?.trim()) errors.push(`${label}: name is required`);
  if (!row.type) errors.push(`${label}: type is required`);
  else if (!(ORDER_PRODUCT_TYPES as readonly string[]).includes(row.type))
    errors.push(`${label}: type ${row.type} is not an order product type`);
  const quantity = toNumber(row.quantity);
  if (quantity === null || quantity <= 0) errors.push(`${label}: quantity must be greater than 0`);
  if (!row.unit) errors.push(`${label}: unit is required`);
  if (toNumber(row.unitPriceAmount) === null) errors.push(`${label}: unit price is required`);
  if (toNumber(row.totalPriceAmount) === null) errors.push(`${label}: total price is required`);
  if (!row.currency) errors.push(`${label}: currency is required`);
  const taxClass = toNumber(row.taxClass);
  if (taxClass === null || !Number.isInteger(taxClass) || taxClass < 0 || taxClass > 100)
    errors.push(`${label}: tax class must be a whole number between 0 and 100`);
  return errors;
}

/** `paymentAttributes` for an add or an update, all fields present as order-service requires. */
export function toPaymentAttributes(row: EditableProduct): Record<string, unknown> {
  const total = Number(row.totalPriceAmount);
  const taxClass = Number(row.taxClass);
  return {
    description: row.name,
    currencyType: row.currency,
    taxClass,
    unitPrice: money(Number(row.unitPriceAmount), row.currency),
    netPrice: money(netFromGross(total, taxClass), row.currency, 3),
    totalPrice: money(total, row.currency),
    quantity: Number(row.quantity),
    unit: row.unit,
  };
}

/**
 * Translates the editor rows into the three amendment lists. Unchanged existing rows are
 * not sent. Invalid rows produce `errors` and no lists, so the caller blocks the preview
 * instead of letting order-service answer "One or more fields failed validation."
 */
export function buildProductAmendments(rows: EditableProduct[]): ProductAmendments {
  const result: ProductAmendments = {
    updateProducts: [],
    addProducts: [],
    removeProducts: [],
    errors: [],
  };

  rows.forEach((row, index) => {
    const label = row.name?.trim() ? `"${row.name.trim()}"` : `product ${index + 1}`;
    if (row._operation === 'remove') {
      if (!row.productExternalId)
        result.errors.push(`${label}: cannot remove a product without an id`);
      else result.removeProducts.push({ productExternalId: row.productExternalId });
      return;
    }
    if (row._operation === 'add') {
      const errors = validateRow(row, label);
      if (errors.length) {
        result.errors.push(...errors);
        return;
      }
      result.addProducts.push({
        productExternalId: row.productExternalId || newProductExternalId(row.name),
        name: row.name.trim(),
        productType: row.type,
        paymentAttributes: toPaymentAttributes(row),
      });
      return;
    }
    if (!isRowChanged(row) && row._operation !== 'update') return;
    if (!row.productExternalId) {
      result.errors.push(`${label}: cannot update a product without an id`);
      return;
    }
    const errors = validateRow(row, label);
    if (errors.length) {
      result.errors.push(...errors);
      return;
    }
    result.updateProducts.push({
      productExternalId: row.productExternalId,
      patches: {
        name: row.name.trim(),
        productType: row.type,
        paymentAttributes: toPaymentAttributes(row),
      },
    });
  });

  if (result.errors.length) {
    return { updateProducts: [], addProducts: [], removeProducts: [], errors: result.errors };
  }
  return result;
}
